/**
 * Chatbot Data Service
 * 
 * Centralized service for all chatbot data fetching operations.
 * Provides a single source of truth for all Supabase queries related to chatbots.
 */

import { supabase } from '@/integrations/supabase/client';
import { validateChatbotId } from '@/utils/chatbotUtils';
import { transformThemeData } from '@/utils/themeUtils';
import { Question, TriggerRule } from '@/types/theme';
import { Json } from '@/integrations/supabase/types';
import { FieldDictionary } from '@/types/fieldsDictionary';

export interface ChatbotData {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  welcome_message: string;
  completion_message: string;
  title: string;
  avatar_url: string | null;
  operator_name: string | null;
  operator_avatar_url: string | null;
  company_name: string | null;
  company_logo: string | null;
  settings: Json | null;
  active: boolean | null;
  custom_javascript: string | null;
  custom_html: string | null;
  created_at: string;
  updated_at: string;
}

export interface ThemeData {
  id: string;
  chatbot_id: string;
  name: string;
  primarycolor: string;
  backgroundcolor: string;
  userbubblecolor: string;
  botbubblecolor: string;
  usertextcolor: string;
  bottextcolor: string;
  headercolor: string;
  headertextcolor: string;
  borderradius: string;
  fontfamily: string;
  buttoncolor: string;
  buttontextcolor: string;
  position: string;
  buttontext: string;
  welcomebubblecolor: string;
  welcomebubbletextcolor: string;
  inputbordercolor: string;
  inputborderradius: string;
  inputbackgroundcolor: string;
  avatarbackgroundcolor: string;
  headerbackgroundcolor: string;
  showavatar: boolean;
  showtitle: boolean;
  showclosebutton: boolean;
  showreset: boolean;
  showsources: boolean;
  showtimestamp: boolean;
  showtypingindicator: boolean;
  showuseravatar: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuestionData {
  id: string;
  chatbot_id: string;
  text: string;
  type: string;
  order: number;
  options: Json | null;
  shortname: string | null;
  errormessage: string | null;
  responsevalidation: string | null;
  validationType: string;
  enable_ai_response: boolean;
  ai_response_prompt: string | null;
  rich_content: Json | null;
  created_at: string;
  updated_at: string;
}

export interface TriggerRuleData {
  id: string;
  chatbot_id: string;
  name: string;
  notes?: string;
  conditions: Json;
  webhookurl?: string;
  postvariablemappings?: Json;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChatbotFullData {
  chatbot: ChatbotData;
  theme: ThemeData | null;
  questions: QuestionData[];
  triggerRules: TriggerRuleData[];
}

/**
 * Fetches chatbot data by ID
 */
export async function fetchChatbot(chatbotId: string): Promise<ChatbotData> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { data, error } = await supabase
    .from('chatbots')
    .select('*')
    .eq('id', validId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('Chatbot not found');
    }
    throw new Error(`Failed to load chatbot: ${error.message}`);
  }

  if (!data) {
    throw new Error('Chatbot not found');
  }

  return data as ChatbotData;
}

/**
 * Fetches theme data for a chatbot
 */
export async function fetchTheme(chatbotId: string): Promise<ThemeData | null> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { data, error } = await supabase
    .from('themes')
    .select('*')
    .eq('chatbot_id', validId)
    .single();

  if (error) {
    // Theme might not exist, return null instead of throwing
    if (error.code === 'PGRST116') {
      return null;
    }
    throw error;
  }

  return data as ThemeData | null;
}

/**
 * Fetches questions for a chatbot
 */
export async function fetchQuestions(chatbotId: string): Promise<QuestionData[]> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('chatbot_id', validId)
    .order('order', { ascending: true });

  if (error) throw error;
  return (data || []) as QuestionData[];
}

/**
 * Fetches trigger rules for a chatbot
 */
export async function fetchTriggerRules(chatbotId: string): Promise<TriggerRuleData[]> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    return [];
  }

  const { data, error } = await supabase
    .from('trigger_rules')
    .select('*')
    .eq('chatbot_id', validId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching trigger rules:', error);
    throw error;
  }

  return (data || []) as TriggerRuleData[];
}

/**
 * Fetches all chatbot data in parallel (optimized)
 */
export async function fetchChatbotFullData(chatbotId: string): Promise<ChatbotFullData> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  // Fetch all data in parallel
  const [
    { data: chatbot, error: chatbotError },
    { data: theme, error: themeError },
    { data: questions, error: questionsError },
    { data: triggerRules, error: triggerRulesError }
  ] = await Promise.all([
    supabase.from('chatbots').select('*').eq('id', validId).single(),
    supabase.from('themes').select('*').eq('chatbot_id', validId).single(),
    supabase.from('questions').select('*').eq('chatbot_id', validId).order('order', { ascending: true }),
    supabase.from('trigger_rules').select('*').eq('chatbot_id', validId).order('created_at', { ascending: true })
  ]);

  // Handle chatbot error (required)
  if (chatbotError) {
    if (chatbotError.code === 'PGRST116') {
      throw new Error('Chatbot not found');
    }
    throw new Error(`Failed to load chatbot: ${chatbotError.message}`);
  }

  if (!chatbot) {
    throw new Error('Chatbot not found');
  }

  // Handle theme error (optional)
  if (themeError && themeError.code !== 'PGRST116') {
    console.error('Error fetching theme:', themeError);
  }

  // Handle questions error
  if (questionsError) {
    console.error('Error fetching questions:', questionsError);
    throw questionsError;
  }

  // Handle trigger rules error
  if (triggerRulesError) {
    console.error('Error fetching trigger rules:', triggerRulesError);
    throw triggerRulesError;
  }

  return {
    chatbot: chatbot as ChatbotData,
    theme: (theme as ThemeData | null) || null,
    questions: (questions || []) as QuestionData[],
    triggerRules: (triggerRules || []) as TriggerRuleData[]
  };
}

/**
 * Fetches active chatbot for widget embedding
 */
export async function fetchActiveChatbot(chatbotId: string): Promise<ChatbotData> {
  const validId = validateChatbotId(chatbotId);
  if (!validId) {
    throw new Error('Invalid chatbot ID format');
  }

  const { data, error } = await supabase
    .from('chatbots')
    .select('*')
    .eq('id', validId)
    .eq('active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      throw new Error('Active chatbot not found');
    }
    throw new Error(`Failed to load chatbot: ${error.message}`);
  }

  if (!data) {
    throw new Error('Active chatbot not found');
  }

  return data as ChatbotData;
}

/**
 * Fetches all fields dictionary entries
 */
export async function fetchFieldsDictionary(): Promise<FieldDictionary[]> {
  const { data, error } = await supabase
    .from('fields_dictionary')
    .select('*')
    .order('key', { ascending: true });

  if (error) {
    console.error('Error fetching fields dictionary:', error);
    throw error;
  }

  return (data || []) as FieldDictionary[];
}

